/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { GmcError, GmcNotificationAction } from '@app/core/types';
import { createAction, props } from '@ngrx/store';
import {
  ActionName,
  ActionResponse,
  ClusterAction,
  ClusterActionResponse,
  ResponseStatus,
} from '@shared/types/cluster-actions';
import {
  FrontendQueryParameters,
  FrontendQueryString,
  Query,
  QueryExportMessage,
  QueryId,
  QueryResponseResultId,
  QueryType,
  SqlMetadata,
  SqlQueryAdvancedSettings,
} from '@shared/types/queries';
import {
  CreateQuery3xResponseBody,
  Query3xExecutionResult,
  Query3xExecutionResultError,
  QueryRunId3x,
  RunningQueryId3x,
} from '@shared/types/queries-3x';

export const query = createAction('[SQL] Query', props<{ data: Query }>());
export const renameQuery = createAction('[SQL] Rename Query', props<{ id: string }>());
export const copyQuery = createAction('[SQL] Copy Query', props<{ id: string }>());
export const removeQueryIntent = createAction('[SQL] Remove query', props<{ id: string }>());
export const removeQuery = createAction('[SQL] Remove query confirmed', props<{ id: string }>());
export const removeQueryOk = createAction('[SQL] Remove query success', props<{ id: string }>());
export const removeQueryFailure = createAction('[SQL] Remove query failure', props<GmcError>());
export const updateQueryFailure = createAction('[SQL] Update query failure', props<GmcError>());
export const cancelCurrentQuery = createAction('[SQL] Cancel current query');
export const cancelQuery = createAction('[SQL] Cancel query', props<{ id: string }>());
export const cancelQueryFailure = createAction('[SQL] Cancel query failure', props<GmcError>());
export const cancelQuerySuccess = createAction('[SQL] Cancel query success', props<{ id: string }>());
export const clearQueryResult = createAction('[SQL] Clear query result', props<{ id: string }>());
export const response = createAction(
  '[SQL] Query Response',
  props<{
    response: ClusterActionResponse<ActionName.SCAN_QUERY_EXECUTE | ActionName.SQL_QUERY_EXECUTE>;
    queryId: string;
    queryType: QueryType;
    titles: string[];
  }>(),
);
export const createQuery3xSuccess = createAction(
  '[SQL] Create query result 3x',
  props<{ response: CreateQuery3xResponseBody; queryId: string; queryRunId: QueryRunId3x }>(),
);

export const newQueryByIdAndType = createAction(
  '[SQL] New Query by Id and Type',
  props<{ id: string; queryType: QueryType }>(),
);

export const executeQuery = createAction('[SQL] Execute Query');

export const executePartialQuery = createAction('[SQL] Execute Partial Query', props<{ query: FrontendQueryString }>());

export const runScanQuery = createAction('[SQL] Run Scan Query', props<{ cacheName: string }>());
export const queryActions = createAction(
  '[SQL] Status query actions',
  props<{
    actions: ClusterAction<ActionName.SCAN_QUERY_EXECUTE | ActionName.SQL_QUERY_EXECUTE>[];
    status: ResponseStatus;
  }>(),
);
export const queriesEnter = createAction('[SQL] Enter queries page');
export const queriesLeave = createAction('[SQL] Leave queries page');
export const getNextResponsePage = createAction('[SQL] Get next page of response', props<{ cursorId: string }>());
export const responseNextPage = createAction(
  '[SQL] Next page of response',
  props<{
    cursorId: string;
    response: ActionResponse[ActionName.QUERY_NEXT_PAGE];
    queryId: QueryId;
  }>(),
);
export const responseNextPage3x = createAction(
  '[SQL] Next page of 3x response',
  props<{ cursorId: string; response: Query3xExecutionResult; queryId: QueryId; queryRunId: QueryRunId3x }>(),
);
export const responseNextPage3xErr = createAction(
  '[SQL] Next page of 3x response error',
  props<{ response: Query3xExecutionResultError; cursorId: string; queryId: QueryId; queryRunId: QueryRunId3x }>(),
);
export const getNextPageFailure = createAction(
  '[SQL] Next page of response failure',
  props<GmcError & { queryId: string; cursorId: string }>(),
);
export const getNextPageFailure3x = createAction(
  '[SQL] Next page of 3x response failure',
  props<GmcError & { queryId: string; cursorId: string; queryRunId: QueryRunId3x }>(),
);
export const removeResponseItem = createAction(
  '[SQL] Remove response item',
  props<{ queryId: string; resultId: QueryResponseResultId }>(),
);

export const queries = createAction('[SQL] Load Queries Success', props<{ list: Array<Query> }>());
export const loadQueriesFailure = createAction('[SQL] Load Queries Failure', props<GmcError>());
export const newQueryFailure = createAction('[SQL] Create Query Failure', props<GmcError>());
export const executeQueryFailure = createAction('[SQL] Execute query failure', props<GmcError>());
export const executeQuery3xStarted = createAction('[SQL] Execute query 3x started', props<{ queryId: QueryId }>());
export const executeQuery3xFailure = createAction(
  '[SQL] Execute query 3x failure',
  props<GmcError & { queryId: QueryId; queryRunId: QueryRunId3x }>(),
);
export const changeCurrentQueryParameters = createAction(
  '[SQL] Change Current Query Parameters',
  props<{ parameters: FrontendQueryParameters }>(),
);
export const sqlMetadata = createAction('[SQL] Sql Metadata', props<{ data: SqlMetadata[]; clusterId: string }>());
export const runningQueriesFailure = createAction('[SQL] Running queries failure', props<GmcError>());
export const toggleAdvancedSettings = createAction('[SQL] Toggle Advanced Settings');
export const toggleParameters = createAction('[SQL] Toggle Parameters');
export const changeCurrentQueryAdvancedSettings = createAction(
  '[SQL] Change current query advanced settings',
  props<{ settings: SqlQueryAdvancedSettings }>(),
);
export const explainQuery = createAction('[Query Statistics] Explain query', props<{ query: string; is3x?: true }>());
export const newQuery = createAction('[SQL] New Query', props<{ data: Query }>());
export const queryStatisticsFailure = createAction('[Query Statistics] Failure', props<GmcError>());
export const selectAllColumnsFromTable = createAction(
  '[SQL] Select all columns from table',
  props<{ clusterId: string; schemaName: string; tableName: string }>(),
);
export const selectColumnFromTable = createAction(
  '[SQL] Select column from table',
  props<{ clusterId: string; schemaName: string; tableName: string; columnName: string }>(),
);

export const killQueriesIntent = createAction('[SQL] Kill queries intent', props<{ ids: string[] }>());
export const killQuery = createAction('[SQL] Kill queries', props<{ id: string }>());
export const killQueryFailure = createAction('[SQL] Kill query failure', props<GmcError>());
export const killQuerySuccess = createAction('[SQL] Kill query success', props<GmcNotificationAction>());

export const executeExportQuery = createAction('[SQL] Execute Export Query');
export const executeExportPartialQuery = createAction(
  '[SQL] Execute Export Partial Query',
  props<{ query: FrontendQueryString }>(),
);

export const executeExportQuerySuccess = createAction(
  '[SQL] Execute Export Query Success',
  props<{ resourceId: string } & GmcNotificationAction>(),
);
export const executeExportQueryFailure = createAction('[SQL] Execute Export Query Failure', props<GmcError>());
export const downloadExportQueryFailure = createAction('[SQL] Download Export Query Failure', props<GmcError>());
export const queryExports = createAction(
  '[SQL] Status Query Exports',
  props<{ exports: QueryExportMessage[] | QueryExportMessage }>(),
);
export const cancelExportQuerySuccess = createAction(
  '[SQL] Cancel Export Query Success',
  props<{ message: QueryExportMessage }>(),
);

export const getQueryInfoErr = createAction('[SQL] Load Query session info error', props<GmcError>());
export const getQuerySessionsErr = createAction('[SQL] Load Query sessions error', props<GmcError>());

export const updateRunningQueryConfiguration = createAction(
  '[SQL] Update running query configuration',
  props<{ duration: number }>(),
);
export const updateRunningQueryConfigurationOK = createAction(
  '[SQL] Update running query configuration OK',
  props<GmcNotificationAction>(),
);
export const updateRunningQueryConfigurationErr = createAction(
  '[SQL] Update running query configuration Err',
  props<GmcError>(),
);

export const getRunningQueryConfigurationErr = createAction(
  '[SQL] Get running query configuration Err',
  props<GmcError>(),
);

export const cancelQuery3x = createAction('[SQL] Cancel query 3x', props<{ id: RunningQueryId3x }>());
export const cancelQuery3xOK = createAction('[SQL] Cancel query 3x OK', props<GmcNotificationAction>());
export const cancelQuery3xErr = createAction('[SQL] Cancel query 3x Err', props<GmcError>());
